import { useAuthStore } from '../store/authStore'
import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import LevelRoadmap from '../components/LevelRoadmap'
import CrowdLanguagePicker from '../components/CrowdLanguagePicker'

export default function ProfilePage() {
  const { t } = useTranslation('worker')
  const { worker, stats, fetchStats, logout } = useAuthStore()
  const [homeScreenStatus, setHomeScreenStatus] = useState<string | null>(null)

  const checkHomeScreen = useCallback(() => {
    const tg = window.Telegram?.WebApp
    if (tg && tg.isVersionAtLeast?.('8.0')) {
      tg.checkHomeScreenStatus?.((status: string) => {
        setHomeScreenStatus(status)
      })
    }
  }, [])

  useEffect(() => {
    fetchStats()
    checkHomeScreen()
  }, [fetchStats, checkHomeScreen])

  const handleAddToHomeScreen = () => {
    window.Telegram?.WebApp?.addToHomeScreen?.()
    setTimeout(checkHomeScreen, 2000)
  }

  const xpInLevel = stats ? stats.xp - stats.current_level_xp : 0
  const xpNeeded = stats?.next_level_xp
    ? stats.next_level_xp - stats.current_level_xp
    : 0
  const xpProgress = xpNeeded > 0 ? Math.min(xpInLevel / xpNeeded, 1) : 1

  return (
    <div className="min-h-screen p-4 pb-24 safe-area-bottom" style={{ background: 'var(--app-page-bg)' }}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold" style={{ color: 'var(--tg-theme-text-color)' }}>
          {t('profile.title')}
        </h1>
        <CrowdLanguagePicker />
      </div>

      {/* Add to Home Screen */}
      {homeScreenStatus && homeScreenStatus !== 'added' && homeScreenStatus !== 'unsupported' && (
        <button
          className="w-full rounded-2xl p-4 mb-3 flex items-center gap-3 text-left"
          style={{ background: 'var(--app-card-bg)' }}
          onClick={handleAddToHomeScreen}
        >
          <span className="text-2xl">📲</span>
          <div className="flex-1">
            <p className="font-semibold text-sm" style={{ color: 'var(--tg-theme-text-color)' }}>
              {t('profile.addToHomeScreen')}
            </p>
            <p className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
              {t('profile.addToHomeScreenSub')}
            </p>
          </div>
          <span className="text-lg opacity-30" style={{ color: 'var(--tg-theme-text-color)' }}>›</span>
        </button>
      )}

      {/* Avatar + Name */}
      <div className="rounded-2xl p-4" style={{ background: 'var(--app-card-bg)' }}>
        <div className="flex items-center gap-3">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center text-2xl"
            style={{ background: 'var(--app-page-bg)' }}
          >
            {stats ? stats.level_emoji : '👤'}
          </div>
          <div className="flex-1">
            <p className="font-bold text-base" style={{ color: 'var(--tg-theme-text-color)' }}>
              {worker?.first_name} {worker?.last_name}
            </p>
            {worker?.telegram_username && (
              <p className="text-sm opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
                @{worker.telegram_username}
              </p>
            )}
            {stats && (
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{
                    background: 'var(--tg-theme-button-color)',
                    color: 'var(--tg-theme-button-text-color)',
                  }}
                >
                  {stats.level_name}
                </span>
                <span
                  className="text-xs opacity-50"
                  style={{ color: 'var(--tg-theme-text-color)' }}
                >
                  {t('profile.rank', { rank: stats.rank })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {stats && (
        <>
          {/* XP Progress */}
          <div className="rounded-2xl p-4 mt-3" style={{ background: 'var(--app-card-bg)' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold" style={{ color: 'var(--tg-theme-text-color)' }}>
                {t('profile.experience')}
              </span>
              <span className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
                {t('profile.xpFraction', { xp: stats.xp, max: stats.next_level_xp ?? 'MAX' })}
              </span>
            </div>
            <div
              className="w-full h-3 rounded-full overflow-hidden"
              style={{ background: 'var(--app-page-bg)' }}
            >
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${xpProgress * 100}%`,
                  background: 'linear-gradient(90deg, #6366f1, #a855f7)',
                }}
              />
            </div>
          </div>

          {/* Coins Card */}
          <div
            className="rounded-2xl p-4 mt-3 text-white"
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)',
            }}
          >
            <p className="text-xs opacity-75">{t('profile.coinsBalance')}</p>
            <p className="text-3xl font-bold mt-1">🪙 {stats.coins_balance}</p>
            <p className="text-sm opacity-75 mt-1">
              {t('profile.totalEarned', { count: stats.coins_total_earned })}
            </p>
            {stats.coins_pending > 0 && (
              <div
                className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                style={{ background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.9)' }}
              >
                ⏳ {t('profile.coinsPending', { count: stats.coins_pending })}
              </div>
            )}
          </div>

          {/* 2x2 Stats Grid */}
          <div className="grid grid-cols-2 gap-2 mt-3">
            {[
              { label: t('profile.stats.tasksCompleted'), value: stats.tasks_completed, icon: '📋' },
              { label: t('profile.stats.itemsFound'), value: stats.items_found_total, icon: '🔍' },
              { label: t('profile.stats.itemsVerified'), value: stats.items_verified_total, icon: '✅' },
              { label: t('profile.stats.approvalRate'), value: `${stats.approval_rate}%`, icon: '📊' },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl p-3"
                style={{ background: 'var(--app-card-bg)' }}
              >
                <p className="text-lg">{item.icon}</p>
                <p className="font-bold text-lg mt-1" style={{ color: 'var(--tg-theme-text-color)' }}>
                  {item.value}
                </p>
                <p className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
                  {item.label}
                </p>
              </div>
            ))}
          </div>

          {/* Streak */}
          <div className="rounded-2xl p-4 mt-3" style={{ background: 'var(--app-card-bg)' }}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">{stats.streak_days >= 3 ? '🔥' : '📅'}</span>
              <div>
                <p className="font-bold text-lg" style={{ color: 'var(--tg-theme-text-color)' }}>
                  {t('profile.streakDays', { count: stats.streak_days })}
                </p>
                <p className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
                  {stats.streak_days >= 3
                    ? t('profile.streakBonus')
                    : t('profile.streakHint')}
                </p>
              </div>
            </div>
          </div>

          {/* Level Roadmap */}
          <div className="mt-3">
            <LevelRoadmap
              currentLevel={stats.level}
              currentXp={stats.xp}
              nextLevelXp={stats.next_level_xp}
              currentLevelXp={stats.current_level_xp}
            />
          </div>
        </>
      )}

      <button
        className="w-full mt-6 py-3 rounded-2xl text-sm font-medium"
        style={{ background: 'var(--app-card-bg)', color: '#ef4444' }}
        onClick={logout}
      >
        {t('profile.signOut')}
      </button>
    </div>
  )
}
